import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  addCrewInduction,
  addCrewTicket,
  addTicketType,
  crewInductionsQuery,
  crewTicketsQuery,
  ENFORCEMENT,
  enforcementQuery,
  requiredQuery,
  retireCrewInduction,
  retireCrewTicket,
  setEnforcement,
  setRequired,
  ticketTypesQuery,
  uploadCrewCard,
  type Enforcement,
} from '../api/compliance.ts';
import { rolesQuery, type CrewMember } from '../api/crew.ts';
import { openFile } from '../api/documents.ts';
import { dayLabel, dayWithYear } from '../api/quotes.ts';
import { venuesQuery } from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { AddButton, RemoveButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ CMP-03, CMP-05, CMP-07 and VEN-05 at v1.965, compliance part 1: what a crew member holds,
// what venues, roles and jobs require, and where the company refuses a gap. A gap itself is worked
// out by the database and arrives on each shift; nothing here decides one.

/** Expiry in words: "never", "until Fri 9 Oct 2026", or "expired …" when it has passed. */
function expiryLabel(expiresOn: string | null | undefined, today: string): string {
  if (!expiresOn) return 'never expires';
  const label = dayWithYear(expiresOn);
  return expiresOn < today ? `expired ${label}` : `until ${label}`;
}

export function CrewCompliance({
  member,
  mayManage,
  today,
}: {
  member: CrewMember;
  mayManage: boolean;
  today: string;
}) {
  const queryClient = useQueryClient();
  const tickets = useQuery(crewTicketsQuery(member.id));
  const inductions = useQuery(crewInductionsQuery(member.id));
  const [adding, setAdding] = useState<'ticket' | 'induction' | null>(null);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['crew', member.id] });
    // A record changes what every shift of theirs lacks.
    await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    await queryClient.invalidateQueries({ queryKey: ['roster'] });
  };
  const retire = useMutation({
    mutationFn: (run: () => Promise<void>) => run(),
    onSuccess: refresh,
  });

  return (
    <div className="flex flex-col gap-4 rounded-md bg-tile p-4 text-left">
      <section aria-label={`${member.displayName}’s tickets`} className="flex flex-col gap-2">
        <h3 className="kicker">Tickets</h3>
        {tickets.data?.length === 0 ? (
          <p className="text-sm text-ink-soft">None recorded.</p>
        ) : null}
        <ul className="flex flex-col gap-1 text-sm">
          {tickets.data?.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{t.ticketType.name}</span>
              {t.number ? <span>{t.number}</span> : null}
              {t.issuer ? <span className="text-ink-soft">{t.issuer}</span> : null}
              <span className={`tag ${t.expiresOn && t.expiresOn < today ? 'tag-accent-2' : ''}`}>
                {expiryLabel(t.expiresOn, today)}
              </span>
              {t.fileId ? (
                <button type="button" className="link" onClick={() => void openFile(t.fileId!)}>
                  Photo
                </button>
              ) : null}
              {mayManage ? (
                <RemoveButton
                  what={t.ticketType.name}
                  onClick={() => retire.mutate(() => retireCrewTicket(t.id))}
                />
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section aria-label={`${member.displayName}’s inductions`} className="flex flex-col gap-2">
        <h3 className="kicker">Inductions</h3>
        {inductions.data?.length === 0 ? (
          <p className="text-sm text-ink-soft">None recorded.</p>
        ) : null}
        <ul className="flex flex-col gap-1 text-sm">
          {inductions.data?.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2">
              <span className="font-semibold">{i.venue.name}</span>
              <span className="text-ink-soft">done {dayWithYear(i.doneOn)}</span>
              <span className={`tag ${i.expiresOn < today ? 'tag-accent-2' : ''}`}>
                {expiryLabel(i.expiresOn, today)}
              </span>
              {i.fileId ? (
                <button type="button" className="link" onClick={() => void openFile(i.fileId!)}>
                  Photo
                </button>
              ) : null}
              {mayManage ? (
                <RemoveButton
                  what={`${i.venue.name} induction`}
                  onClick={() => retire.mutate(() => retireCrewInduction(i.id))}
                />
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      {retire.isError ? (
        <p role="alert" className="alert">
          {retire.error.message}
        </p>
      ) : null}

      {mayManage && adding === null ? (
        <div className="flex flex-wrap gap-2">
          <AddButton noun="Ticket" variant="quiet" onClick={() => setAdding('ticket')} />
          <AddButton noun="Induction" variant="quiet" onClick={() => setAdding('induction')} />
        </div>
      ) : null}
      {adding === 'ticket' ? (
        <TicketForm
          member={member}
          onDone={async (saved) => {
            setAdding(null);
            if (saved) await refresh();
          }}
        />
      ) : null}
      {adding === 'induction' ? (
        <InductionForm
          member={member}
          today={today}
          onDone={async (saved) => {
            setAdding(null);
            if (saved) await refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function TicketForm({
  member,
  onDone,
}: {
  member: CrewMember;
  onDone: (saved: boolean) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const types = useQuery(ticketTypesQuery);
  const [typeId, setTypeId] = useState('');
  const [newType, setNewType] = useState<string | null>(null);
  const [number, setNumber] = useState('');
  const [issuer, setIssuer] = useState('');
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [never, setNever] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: async () => {
      const ticketTypeId = newType !== null ? (await addTicketType(newType.trim())).id : typeId;
      const fileId = photo ? await uploadCrewCard(member.id, photo) : null;
      await addCrewTicket(member.id, {
        ticketTypeId,
        number: number.trim() || null,
        issuer: issuer.trim() || null,
        issuedOn: issuedOn || null,
        expiresOn: never ? null : expiresOn || null,
        fileId,
      });
      await queryClient.invalidateQueries({ queryKey: ticketTypesQuery.queryKey });
    },
    onSuccess: () => onDone(true),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (newType !== null ? !newType.trim() : !typeId) return setProblem('Choose the ticket.');
    if (!never && !expiresOn) {
      return setProblem('Give the expiry date, or tick “No expiry” (a White Card, say).');
    }
    setProblem(undefined);
    save.mutate();
  }

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-3" aria-label="Ticket">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`ticket-type-${member.id}`} className="text-xs text-ink-soft">
            Ticket
          </label>
          <select
            id={`ticket-type-${member.id}`}
            className="input"
            value={newType !== null ? '+' : typeId}
            onChange={(e) => {
              const v = e.currentTarget.value;
              if (v === '+') return setNewType('');
              setNewType(null);
              setTypeId(v);
            }}
          >
            <option value="">Choose…</option>
            {(types.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            <option value="+">+ New ticket type</option>
          </select>
          {newType !== null ? (
            <Field
              label="New ticket’s name"
              value={newType}
              onChange={(e) => setNewType(e.currentTarget.value)}
            />
          ) : null}
        </div>
        <Field label="Number" value={number} onChange={(e) => setNumber(e.currentTarget.value)} />
        <Field
          label="Issued by"
          value={issuer}
          onChange={(e) => setIssuer(e.currentTarget.value)}
        />
        <Field
          label="Issued on"
          type="date"
          value={issuedOn}
          onChange={(e) => setIssuedOn(e.currentTarget.value)}
        />
        <Field
          label="Expires on"
          type="date"
          disabled={never}
          value={expiresOn}
          onChange={(e) => setExpiresOn(e.currentTarget.value)}
        />
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input
            type="checkbox"
            checked={never}
            onChange={(e) => setNever(e.currentTarget.checked)}
          />
          No expiry
        </label>
      </div>
      <PhotoField id={`ticket-photo-${member.id}`} onChange={setPhoto} />
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={() => void onDone(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function InductionForm({
  member,
  today,
  onDone,
}: {
  member: CrewMember;
  today: string;
  onDone: (saved: boolean) => Promise<void>;
}) {
  const venues = useQuery(venuesQuery);
  const withInduction = (venues.data ?? []).filter((v) => v.induction);
  const [venueId, setVenueId] = useState('');
  const [doneOn, setDoneOn] = useState(today);
  const [photo, setPhoto] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | undefined>();
  const chosen = withInduction.find((v) => v.id === venueId);
  const save = useMutation({
    mutationFn: async () => {
      const fileId = photo ? await uploadCrewCard(member.id, photo) : null;
      await addCrewInduction(member.id, { venueId, doneOn, fileId });
    },
    onSuccess: () => onDone(true),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!venueId) return setProblem('Choose the venue.');
    if (!doneOn) return setProblem('Give the day it was done.');
    setProblem(undefined);
    save.mutate();
  }

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-3" aria-label="Induction">
      {venues.data && withInduction.length === 0 ? (
        <p className="text-sm text-ink-soft">
          No venue has an induction set yet. Add one on the venue under Venues.
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`induction-venue-${member.id}`} className="text-xs text-ink-soft">
            Venue
          </label>
          <select
            id={`induction-venue-${member.id}`}
            className="input"
            value={venueId}
            onChange={(e) => setVenueId(e.currentTarget.value)}
          >
            <option value="">Choose…</option>
            {withInduction.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} — {v.induction!.name}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="Done on"
          type="date"
          hint={chosen ? `It lasts ${chosen.induction!.validMonths} months.` : undefined}
          value={doneOn}
          onChange={(e) => setDoneOn(e.currentTarget.value)}
        />
      </div>
      <PhotoField id={`induction-photo-${member.id}`} onChange={setPhoto} />
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={() => void onDone(false)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function PhotoField({ id, onChange }: { id: string; onChange: (file: File | null) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs text-ink-soft">
        Photo of the card (optional)
      </label>
      <input
        id={id}
        type="file"
        accept="image/jpeg,image/png,application/pdf"
        capture="environment"
        className="text-sm"
        onChange={(e) => onChange(e.currentTarget.files?.[0] ?? null)}
      />
    </div>
  );
}

/**
 * The tickets a venue, a role or a job requires, as a set of checkboxes with a way to add a kind
 * of ticket. Saving replaces the set (CMP-07 at v1.965).
 */
export function RequiredTickets({
  owner,
  id,
  label,
  editable,
}: {
  owner: 'venues' | 'crew-roles' | 'jobs';
  id: string;
  label: string;
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const types = useQuery(ticketTypesQuery);
  const required = useQuery(requiredQuery(owner, id));
  const [editing, setEditing] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [newType, setNewType] = useState('');
  const save = useMutation({
    mutationFn: async () => {
      const extra = newType.trim() ? [(await addTicketType(newType.trim())).id] : [];
      return setRequired(owner, id, [...chosen, ...extra]);
    },
    onSuccess: async () => {
      setEditing(false);
      setNewType('');
      await queryClient.invalidateQueries({ queryKey: ['required-tickets', owner, id] });
      await queryClient.invalidateQueries({ queryKey: ticketTypesQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
    },
  });

  if (!editing) {
    return (
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-soft">{label}:</span>
        {required.data && required.data.length === 0 ? <span>none</span> : null}
        {required.data?.map((t) => (
          <span key={t.id} className="tag">
            {t.name}
          </span>
        ))}
        {editable ? (
          <button
            type="button"
            className="link"
            onClick={() => {
              setChosen((required.data ?? []).map((t) => t.id));
              setEditing(true);
            }}
          >
            Change<span className="sr-only"> {label.toLowerCase()}</span>
          </button>
        ) : null}
      </p>
    );
  }
  return (
    <fieldset className="flex flex-col gap-2 rounded-md bg-tile p-3">
      <legend className="text-sm font-semibold">{label}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
        {(types.data ?? []).map((t) => (
          <label key={t.id} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={chosen.includes(t.id)}
              onChange={(e) => {
                const on = e.currentTarget.checked;
                setChosen((c) => (on ? [...c, t.id] : c.filter((x) => x !== t.id)));
              }}
            />
            {t.name}
          </label>
        ))}
      </div>
      <Field
        label="Add another kind of ticket (optional)"
        value={newType}
        onChange={(e) => setNewType(e.currentTarget.value)}
      />
      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </fieldset>
  );
}

/** CMP-07 at v1.965: each role's tickets, on the Crew page. */
export function RolesTickets({ editable }: { editable: boolean }) {
  const roles = useQuery(rolesQuery);
  return (
    <section className="flex flex-col gap-3" aria-labelledby="role-tickets">
      <h2 id="role-tickets" className="section-title">
        Tickets each role needs
      </h2>
      {roles.data?.length === 0 ? (
        <p className="text-sm text-ink-soft">No roles yet — they’re added as you plan shifts.</p>
      ) : null}
      <ul className="flex flex-col gap-2">
        {roles.data?.map((r) => (
          <li key={r.id}>
            <RequiredTickets owner="crew-roles" id={r.id} label={r.name} editable={editable} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** CMP-07 at v1.965: where the company refuses a gap. */
export function EnforcementSetting({ editable }: { editable: boolean }) {
  const queryClient = useQueryClient();
  const current = useQuery(enforcementQuery);
  const save = useMutation({
    mutationFn: (value: Enforcement) => setEnforcement(value),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: enforcementQuery.queryKey }),
  });
  return (
    <section className="flex flex-col gap-3" aria-labelledby="enforcement">
      <h2 id="enforcement" className="section-title">
        When someone isn’t compliant
      </h2>
      <p className="text-sm text-ink-soft">
        What happens when a shift needs a ticket or induction the person doesn’t hold (or that runs
        out before the shift ends). The gap is always shown; this decides where AVenue refuses.
      </p>
      {!editable && current.data ? <p className="text-sm">{ENFORCEMENT[current.data]}</p> : null}
      {editable ? (
        <fieldset className="flex flex-col gap-2 text-sm">
          <legend className="sr-only">Where a gap is refused</legend>
          {(Object.keys(ENFORCEMENT) as Enforcement[]).map((k) => (
            <label key={k} className="flex items-center gap-2">
              <input
                type="radio"
                name="enforcement"
                checked={current.data === k}
                disabled={save.isPending}
                onChange={() => save.mutate(k)}
              />
              {ENFORCEMENT[k]}
              {k === 'accept' ? <span className="tag">Default</span> : null}
            </label>
          ))}
        </fieldset>
      ) : null}
      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}
    </section>
  );
}
